import { Icon } from "../Icons";
import { OSChooser } from "../OSChooser";
import type { Platform } from "../../lib/release";
import { artifactFilename } from "../../lib/release";
import type { ReleaseState } from "../../hooks/use-release";
import type { DetectedOS } from "../../lib/os-detection";

type FinalCTAProps = {
  chooserOpen: boolean;
  setChooserOpen: (v: boolean) => void;
  onBuyClick: () => void;
  release: ReleaseState;
  detected: DetectedOS;
  onDownload: (platform: Platform) => boolean;
};

export function FinalCTA({
  chooserOpen,
  setChooserOpen,
  onBuyClick,
  release,
  detected,
  onDownload,
}: FinalCTAProps) {
  const ready = release.status === "ready" && release.manifest !== null;
  const loading = release.status === "loading";
  const unavailable =
    release.status === "unavailable" || release.status === "error";
  const detectedArtifact = ready
    ? release.manifest!.artifacts[detected.platform]
    : undefined;
  const detectedFilename =
    ready && release.manifest
      ? artifactFilename(release.manifest, detected.platform)
      : undefined;

  const handleMainClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!ready || !detectedArtifact) {
      e.preventDefault();
      return;
    }
    const ok = onDownload(detected.platform);
    if (!ok) e.preventDefault();
  };
  return (
    <section className="section grid-bg" id="final-cta">
      <div className="section-inner" style={{ textAlign: "center" }}>
        <h2
          style={{
            fontSize: 52,
            letterSpacing: "-0.03em",
            fontWeight: 500,
            lineHeight: 1.05,
          }}
        >
          Native signal processing for Ignition, controlled with{" "}
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              background: "#FBF6DA",
              padding: "0 10px",
              borderRadius: 4,
              fontSize: 46,
              fontWeight: 500,
            }}
          >
            SQL
          </span>
          .
        </h2>
        <p style={{ fontSize: 17, color: "var(--ink-3)", marginTop: 18 }}>
          Free to try. Install via{" "}
          <span className="mono" style={{ fontSize: 14 }}>
            Platform &gt; Modules
          </span>
          . Ignition 8.3.0+.
        </p>

        <div style={{ display: "inline-flex", gap: 12, marginTop: 32, position: "relative", flexWrap: "wrap", justifyContent: "center" }}>
          <div style={{ position: "relative" }}>
            <div
              className="btn btn-primary btn-xl"
              style={{
                padding: 0,
                overflow: "hidden",
                opacity: unavailable ? 0.55 : 1,
              }}
            >
              <a
                href={detectedArtifact?.url || "#"}
                download={detectedFilename}
                onClick={handleMainClick}
                aria-disabled={!ready}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "16px 14px 16px 22px",
                  color: "inherit",
                  textDecoration: "none",
                  cursor: ready ? "pointer" : "not-allowed",
                  fontWeight: "inherit",
                }}
              >
                <Icon.Download />
                Download
                {loading && (
                  <span
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      marginLeft: 6,
                      border: "2px solid rgba(0,0,0,.15)",
                      borderTopColor: "rgba(0,0,0,.55)",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                )}
              </a>
              <button
                type="button"
                aria-label="Choose platform"
                onClick={() => setChooserOpen(!chooserOpen)}
                style={{
                  background: "transparent",
                  border: "none",
                  borderLeft: "1px solid rgba(0,0,0,.15)",
                  padding: "16px 18px 16px 14px",
                  cursor: "pointer",
                  color: "inherit",
                  opacity: 0.7,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Icon.Caret />
              </button>
            </div>
            <OSChooser
              open={chooserOpen}
              onDismiss={() => setChooserOpen(false)}
              detectedPlatform={detected.platform}
              manifest={release.manifest}
              onDownload={onDownload}
            />
            {unavailable && (
              <div
                className="mono"
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  fontSize: 10,
                  color: "var(--ink-3)",
                  whiteSpace: "nowrap",
                }}
              >
                {release.status === "error"
                  ? "couldn't load releases — try again later"
                  : "first release pending"}
              </div>
            )}
          </div>
          <button className="btn btn-secondary btn-xl" onClick={onBuyClick}>
            Buy a license <Icon.External style={{ opacity: 0.6 }} />
          </button>
        </div>

      </div>
    </section>
  );
}
